import { fromBaseRecord, assertUncomputedFromBase } from '../janus-engine/compile/from-base-context';
import type { InventorySharedCatalog } from '../janus-engine/compile/inventory-shared-catalog';
import type { FromBaseRow } from '../janus-engine/compile/from-base-context';
import { DEFINITION_FAMILY_SCHEMA } from './definition-family-cells';
import type { DefinitionFamilyGraph } from './definition-family-cells';
import { DEFINITION_LIST_FAMILIES, definitionListFamily } from './definition-list-families';
import type { DefinitionListPresentation } from './definition-list-families';
import type { CatalogTableResponse } from './types';
import { definitionNeedsFinalization } from '../janus-engine/compile/definition-finalization-needs';
import type { DefinitionFinalizationReads, DefinitionFinalizationMetadata } from '../janus-engine/compile/definition-finalization-needs';

export const DEFINITION_LIST_SCHEMA = 'definition-list-v1';
export interface DefinitionListResponse {
    schema: typeof DEFINITION_LIST_SCHEMA;
    family: string;
    item_type: string;
    definition_ids: string[];
    base: FromBaseRow;
    total: number;
}

/** Only saved definition endpoints opt in. Full-input and legacy URLs retain
 * their selection and cache identity, including standard_object. */
export function definitionListDataUrl(endpoint: string): string {
    const url = new URL(endpoint, window.location.href);
    const family = url.pathname.match(/\/options\/api\/([a-z_]+)_list_inc\.php$/)?.[1];
    if (!family || !Object.prototype.hasOwnProperty.call(DEFINITION_LIST_FAMILIES, family)) return endpoint;
    const spec = definitionListFamily(family, url.searchParams.get('item_type') ?? '', { background_essences: [] });
    if (spec.finalize) return endpoint;
    const reads = definitionListFinalizationReads({ columns: spec.columns, options: {
        ...(spec.filters ? { filters: spec.filters } : {}), ...(spec.order ? { order: spec.order } : {}),
    } });
    // Pure item-field tables can decide from their held rows alone (including
    // the unconditional enchantment check). Preserve their exact HEAD payload.
    if (!reads.allLayers && !reads.pictures && reads.layers.length === 0) return endpoint;
    url.searchParams.set('finalization_meta', '1');
    return url.href;
}

/** Inspect the existing descriptors, including filters/order and implicit
 * projectors. Unknown calculation inputs conservatively observe every layer. */
export function definitionListFinalizationReads(graph: Pick<DefinitionFamilyGraph, 'columns' | 'options'>): DefinitionFinalizationReads {
    const layers = new Set<string>(), nestedReferences = new Set<string>();
    let pictures = false, allLayers = false;
    const visit = (value: unknown): void => {
        if (!value || typeof value !== 'object') return;
        if (fromBaseRecord(value)) {
            if (value.from === 'layer' && typeof value.key === 'string') layers.add(value.key);
            if (value.kind === 'media' && value.icon_only !== true) pictures = true;
            if (['pl', 'spell', 'enchantment', 'recipe', 'status', 'defense', 'lock'].includes(String(value.kind))) allLayers = true;
            if (value.kind === 'references' && value.nested_types && fromBaseRecord(value.field)
                && value.field.from === 'layer' && typeof value.field.key === 'string') nestedReferences.add(value.field.key);
        }
        for (const child of Object.values(value)) visit(child);
    };
    visit(graph.columns); visit(graph.options?.filters); visit(graph.options?.order);
    return { layers: [...layers], nestedReferences: [...nestedReferences], pictures, allLayers };
}
/** Build the projector graph after all required saved compiler inputs arrive. */
export function adoptDefinitionList(input: DefinitionListResponse, presentation: DefinitionListPresentation = {}, fullInputs = false): CatalogTableResponse & { client_catalog: DefinitionFamilyGraph } {
    const catalog = input.base?.catalog;
    if (input.schema !== DEFINITION_LIST_SCHEMA || !Array.isArray(input.definition_ids) || !fromBaseRecord(input.base)
        || !fromBaseRecord(catalog) || input.total !== input.definition_ids.length
        || input.definition_ids.some(id => typeof id !== 'string' || !fromBaseRecord(catalog[id])))
        throw new Error('definition-list-envelope');
    assertUncomputedFromBase(input.base);
    const spec = definitionListFamily(input.family, input.item_type, input.base, presentation);
    const finalize = spec.finalize || fullInputs;
    const shared = input.base.definition_shared;
    if (finalize && (!fromBaseRecord(shared) || !fromBaseRecord(shared.base) || !fromBaseRecord(shared.metadata)))
        throw new Error('definition-list-finalizer-inputs:' + input.family);
    return { columns: spec.columns, rows: input.definition_ids.map(item_id => ({ item_id, cells: {} })), total: input.total,
        client_catalog: { schema: DEFINITION_FAMILY_SCHEMA, family: input.family, item_type: input.item_type,
            definition_ids: input.definition_ids, base: input.base, columns: spec.columns,
            ...(finalize ? { shared: shared as unknown as InventorySharedCatalog } : {}),
            options: { ...(!finalize ? { saved_definitions: true as const } : {}),
                ...(fullInputs ? { finalize_definitions: true as const } : {}),
                ...(spec.filters ? { filters: spec.filters } : {}), ...(spec.order ? { order: spec.order } : {}) } } };
}

/** Runs inside the table loader, before its first render. A single exceptional
 * root upgrades the selection to the existing full-input finalization lane. */
export async function prepareDefinitionList(input: DefinitionListResponse, presentation: DefinitionListPresentation = {},
    endpoint?: string, fetchInputs: typeof fetch = fetch): Promise<ReturnType<typeof adoptDefinitionList>> {
    const saved = adoptDefinitionList(input, presentation);
    if (!saved.client_catalog.options?.saved_definitions) return saved;
    const catalog = input.base.catalog as Record<string, FromBaseRow>;
    const rawMetadata = input.base.definition_finalization_metadata;
    const metadata = fromBaseRecord(rawMetadata) && Array.isArray(rawMetadata.objectTypes) && fromBaseRecord(rawMetadata.layerCatalog)
        ? rawMetadata as unknown as DefinitionFinalizationMetadata : undefined;
    const reads = definitionListFinalizationReads(saved.client_catalog);
    if (!input.definition_ids.some(id => definitionNeedsFinalization(catalog[id]!, reads, metadata, catalog))) return saved;
    if (!endpoint) throw new Error('definition-list-finalizer-endpoint:' + input.family);
    const url = new URL(endpoint, window.location.href);
    url.searchParams.set('inputs', 'full');
    const response = await fetchInputs(url.href, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
    const full = await response.json() as DefinitionListResponse & { error?: string; detail?: string };
    if (!response.ok || full.error) throw new Error(full.detail || full.error || `HTTP ${response.status}`);
    if (full.family !== input.family || full.item_type !== input.item_type) throw new Error('definition-list-finalizer-selection');
    return adoptDefinitionList(full, presentation, true);
}
